import { useEffect, useMemo, useState } from 'react'

import { useSavedFilter } from './useSavedFilter'
import { useFacilitySearch } from '@/hooks/useFacilitySearch'
import { FeatureFlag, useFeatureFlag } from '@/hooks/useFeatureFlags'
import { useSelector } from 'react-redux'
import { useDebounce } from 'use-debounce'

import {
  useDevicesFormOptionsLazyQuery,
  useFloorsByFacilityIdsLazyQuery,
  useIncidentFiltersEnrichmentQuery,
  useListIncidentTypesQuery,
} from '@/graphql/generated/hooks'
import { buildFormState } from '@/redux/incidents/conversion'
import {
  aiOperatorStateOptions,
  allSourceTypeOptions,
  clipsOptions,
  createdAtIntervalOptions,
  incidentStatusOptions,
  mutedOptions,
  priorityOptions,
  sourceTypeOptions,
} from '@/redux/incidents/incidentsFilterOptions'
import { selectActiveFilterCriteria } from '@/redux/incidents/incidentsSlice'
import {
  OptionI,
  Options,
  getDeviceOptions,
  getFloorOptions,
  getIncidentFacilityOptionsWithAddress,
  getIncidentTypeFilterOptions,
  getIncidentTypeOptions,
} from '@/utils/forms/optionBuilders'
import { MIN_CHAR_COUNT, SEARCH_DEBOUNCE_TIME } from '@/utils/forms/validation'

import { SavedFilterI } from '../types/generic'
import {
  areAllDefined,
  dummyEntityId,
  getEnrichmentFacilityOptions,
  getMergedFacilityOptions,
  hasFacilitiesOrFloors,
} from '../utils/utils'

interface UseIncidentFilterFormValuesIProps {
  watch: any
  editedFilter: SavedFilterI | null
}

const DEVICE_MIN_SEARCH_LENGTH = 3

export const useIncidentFilterFormValues = ({
  watch,
  editedFilter,
}: UseIncidentFilterFormValuesIProps) => {
  // In regular form state this retrieves criteria saved in Redux state
  // In update filter form this mimics filter state for selected filter
  const criteria = editedFilter
    ? useSavedFilter({ savedFilter: editedFilter })
    : useSelector(selectActiveFilterCriteria)

  const isIncidentTypeDefinition = useFeatureFlag(
    FeatureFlag.useIncidentTypeDefinition
  )
  const isSalesEnv = useFeatureFlag(FeatureFlag.salesDatabaseReset)
  const availableSourceTypeOptions = isSalesEnv
    ? allSourceTypeOptions
    : sourceTypeOptions
  const { data: incidentTypesData } = useListIncidentTypesQuery({
    variables: { archived: false },
    skip: !isIncidentTypeDefinition,
  })

  const { data: enrichmentData } = useIncidentFiltersEnrichmentQuery({
    // For the time being we always want to hide archived results.
    // Maybe in future we can open this up to being a toggle within the search?
    variables: {
      includeArchived: false,
      // This hack is used to bypass the filtering issue
      // When empty array is used as for deviceIdIn all devices are returned instead of no devices
      deviceIdIn: criteria?.deviceIdIn ?? [dummyEntityId],
      // Same idIn: [] footgun as deviceIdIn above — without the sentinel,
      // browse mode's enrichment "pre-selected" facilities would actually be
      // every facility, and getMergedFacilityOptions would then prefer that
      // (subtitle-less) version over the real search results for all of them.
      facilityIdIn: criteria?.facilityIdIn ?? [dummyEntityId],
    },
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all',
    skip: !criteria,
  })

  const [floorFormOptions, setFloorFormOptions] = useState<Options>()
  const [deviceFormOptions, setDeviceFormOptions] = useState<Options>()
  const [deviceSearchValue, setDeviceSearchValue] = useState<string>('')
  const [facilitySearchValue, setFacilitySearchValue] = useState<string>('')
  const [debouncedFacilitySearchValue] = useDebounce(
    facilitySearchValue,
    SEARCH_DEBOUNCE_TIME
  )

  const [getFloorFormOptions, { loading: isFloorLoading }] =
    useFloorsByFacilityIdsLazyQuery()

  const [getDeviceFormOptions, { loading: isDeviceLoading }] =
    useDevicesFormOptionsLazyQuery()

  // Replaces two separate useFacilityNamesLazyQuery effects (an initial
  // unfiltered fetch + a name-only search-effect) with the shared
  // useFacilitySearch hook — reactive, and searches name/short name/address
  // instead of name only. includeArchived: false preserves the original
  // filter.
  const {
    facilitiesData,
    isFacilitiesLoading: isFacilityLoading,
    isError: isFacilityError,
  } = useFacilitySearch({
    searchQuery: debouncedFacilitySearchValue,
    filter: { includeArchived: false },
  })

  const isLoading =
    !criteria ||
    !enrichmentData ||
    (isIncidentTypeDefinition && !incidentTypesData)
  const watchFacility: OptionI[] = watch('facilityName', criteria?.facilityIdIn)
  const watchFloor: OptionI[] = watch('floorName')

  useEffect(() => {
    if (areAllDefined(watchFacility, watchFloor)) {
      if (watchFacility?.[0]?.value) {
        getFloorFormOptions({
          variables: { facilityIds: watchFacility?.map((e) => e.value) },
          onCompleted: (floorsData) => {
            setFloorFormOptions(getFloorOptions(floorsData))
          },
        })
      } else {
        setFloorFormOptions([])
      }
    }
  }, [watchFacility])

  useEffect(() => {
    // If facility or floor is available fetch all devices for that facility and/or floor
    const shouldUseFacilitiesOrFloors =
      hasFacilitiesOrFloors(watchFacility, watchFloor) &&
      areAllDefined(watchFacility, watchFloor)
    // Otherwise use the search term if it has more than three characters
    const shouldUseSearchValue =
      !shouldUseFacilitiesOrFloors &&
      deviceSearchValue.length >= DEVICE_MIN_SEARCH_LENGTH

    if (shouldUseFacilitiesOrFloors || shouldUseSearchValue) {
      getDeviceFormOptions({
        variables: {
          filter: {
            ...(shouldUseFacilitiesOrFloors && {
              facilityIdIn: watchFacility?.map((f) => f.value) ?? [],
            }),
            ...(shouldUseFacilitiesOrFloors && {
              floorIdIn: watchFloor?.map((f) => f.value) ?? [],
            }),
            ...(shouldUseSearchValue && { nameLike: deviceSearchValue }),
          },
        },
        onCompleted: (devicesData) => {
          setDeviceFormOptions(getDeviceOptions(devicesData))
        },
      })
    } else {
      // Don't show any options
      setDeviceFormOptions([])
    }
  }, [watchFacility, watchFloor, deviceSearchValue])

  // Below MIN_CHAR_COUNT (2 — previously 1 here specifically), useFacilitySearch
  // is in browse mode and facilityOptionsFromSearch is just the unfiltered
  // list, so merge in enrichmentData to guarantee pre-selected facilities stay
  // visible even if they're not in the first page. At/above MIN_CHAR_COUNT,
  // facilityOptionsFromSearch already reflects the actual search results.
  const facilityOptionsFromSearch = useMemo(
    () =>
      getIncidentFacilityOptionsWithAddress(
        facilitiesData,
        true,
        debouncedFacilitySearchValue
      ),
    [facilitiesData, debouncedFacilitySearchValue]
  )
  const facilityNameOptions = useMemo(() => {
    if (debouncedFacilitySearchValue.length >= MIN_CHAR_COUNT) {
      return facilityOptionsFromSearch
    }
    const merged = getMergedFacilityOptions(
      getEnrichmentFacilityOptions(enrichmentData),
      facilityOptionsFromSearch
    )
    if (!facilitySearchValue) return merged
    // Below MIN_CHAR_COUNT, useFacilitySearch stays in browse mode (a capped,
    // unfiltered page) and the facility row always searches "externally" (no
    // client-side re-filter of its own), so a short query like a single
    // character would otherwise show that page completely unfiltered.
    // Narrow it by label locally so a short query still narrows *something*
    // visible, even though a real server search only starts at
    // MIN_CHAR_COUNT — this is a client-side narrowing of the already-loaded
    // page, not a network search, so it reacts to the raw (non-debounced)
    // value instead of waiting on debouncedFacilitySearchValue.
    const lowerSearch = facilitySearchValue.toLowerCase()
    return merged.filter((option) =>
      option.label.toLowerCase().includes(lowerSearch)
    )
  }, [
    facilitySearchValue,
    debouncedFacilitySearchValue,
    facilityOptionsFromSearch,
    enrichmentData,
  ])

  const incidentTypeDefinitionOptions = useMemo(
    () =>
      isIncidentTypeDefinition
        ? getIncidentTypeFilterOptions(incidentTypesData)
        : undefined,
    [isIncidentTypeDefinition, incidentTypesData]
  )

  const options = {
    createdAtOptions: createdAtIntervalOptions,
    deviceNameOptions: deviceFormOptions,
    facilityNameOptions,
    floorNameOptions: floorFormOptions,
    incidentStatusOptions: incidentStatusOptions,
    incidentTypeOptions:
      incidentTypeDefinitionOptions ?? getIncidentTypeOptions(enrichmentData),
    mutedOptions: mutedOptions,
    priorityOptions: priorityOptions,
    sourceTypeOptions: availableSourceTypeOptions,
    clipsOptions: clipsOptions,
    aiOperatorStateOptions: aiOperatorStateOptions,
  }

  const defaultValues = buildFormState(
    criteria,
    enrichmentData,
    incidentTypeDefinitionOptions,
    availableSourceTypeOptions
  )

  if (isLoading) {
    return {
      defaultValues: null,
      options: null,
      deviceSearchValue: null,
      onDeviceSearchChange: null,
      facilitySearchValue: null,
      onFacilitySearchChange: null,
      isDeviceLoading: null,
      isFloorLoading: null,
      isFacilityLoading: null,
      isFacilityError: null,
    }
  }

  return {
    defaultValues,
    options,
    deviceSearchValue,
    onDeviceSearchChange: hasFacilitiesOrFloors(watchFacility, watchFloor)
      ? null
      : setDeviceSearchValue,
    facilitySearchValue,
    onFacilitySearchChange: setFacilitySearchValue,
    isDeviceLoading,
    isFloorLoading,
    isFacilityLoading,
    isFacilityError,
  }
}
